import {
  Alert,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getImportFileErrorMessage, useUploadScreeningFile } from '../../../api/created/importFile.ts';
import { ScreeningCaseListItemDto } from '../../../api/created/screening.ts';
import { FileInput } from '../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getScreeningImportMessage } from '../../../utils/screeningImportCode.ts';
import { ShowCountryCodesDialog } from '../../contact-tracing/components/ShowCountyCodesDialog.tsx';
import { SelectScreeningCase } from './SelectScreeningCase.tsx';

interface FormValues {
  screeningCase: ScreeningCaseListItemDto | null;
  file: File | null;
}

export function ImportScreeningDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      screeningCase: null,
      file: null,
    },
  });

  const fileValue = watch('file');
  const screeningCaseValue = watch('screeningCase');
  const [isCountriesDialogOpen, setIsCountriesDialogOpen] = useState(false);
  const navigate = useNavigate();
  const uploadScreeningFileMutation = useUploadScreeningFile();
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      reset({ screeningCase: null, file: null });
      setUploadErrorMessage(null);
    }
  }, [open]);

  useEffect(() => {
    setUploadErrorMessage(null);
  }, [fileValue, screeningCaseValue]);

  const handleOnClose = () => {
    reset({ screeningCase: null, file: null });
    setUploadErrorMessage(null);
    onClose();
  };

  const onSubmit = async ({ screeningCase, file }: FormValues) => {
    setUploadErrorMessage(null);
    try {
      const response = await uploadScreeningFileMutation.mutateAsync({
        screeningCaseId: screeningCase!.screeningCaseId,
        file: file!,
      });
      if (!response.success || !response.fileId) {
        setUploadErrorMessage(getScreeningImportMessage(response.code));
        return;
      }
      handleOnClose();
      void navigate({
        to: '/review/$fileId',
        params: { fileId: response.fileId },
        search: { screeningCaseId: screeningCase!.screeningCaseId },
      });
    } catch (error) {
      setUploadErrorMessage(getImportFileErrorMessage(error, undefined, getScreeningImportMessage));
    }
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 1200 }}>
        <ModalClose />
        <Typography level={'h4'}>Screening-Personen hinzufügen</Typography>
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <Stack direction={'row'} gap={2}>
              <FormControl sx={{ flex: 1 }} error={!!errors.screeningCase}>
                <FormLabel>
                  <Typography>Screening-Auftrag *</Typography>
                </FormLabel>
                {/* Persons are only added to active screening cases. */}
                <Controller
                  name={'screeningCase'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value } }) => (
                    <SelectScreeningCase value={value} onChange={onChange} activeOnly enabled={open} />
                  )}
                />
                {errors.screeningCase && <FormHelperText>Screening-Auftrag muss angegeben werden</FormHelperText>}
              </FormControl>

              <FormControl sx={{ flex: 1 }} error={!!errors.file}>
                <FormLabel>
                  <Typography>Datei *</Typography>
                </FormLabel>
                <Controller
                  name={'file'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <FileInput
                      value={value}
                      onChange={onChange}
                      accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                      placeholder={'Datei hochladen'}
                    />
                  )}
                />
                {errors.file && <FormHelperText>Datei muss ausgewählt werden</FormHelperText>}
                <Button
                  variant={'soft'}
                  component={'a'}
                  target={'_blank'}
                  href={'screening_import.xlsx'}
                  sx={{ my: 1 }}>
                  Beispiel-Datei herunterladen
                </Button>
                <Button variant={'soft'} onClick={() => setIsCountriesDialogOpen(true)}>
                  ISO-Ländercodes anzeigen
                </Button>
              </FormControl>
            </Stack>

            {uploadErrorMessage && (
              <Alert color={'danger'} startDecorator={<Icons.ErrorOutline />}>
                {uploadErrorMessage}
              </Alert>
            )}

            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={uploadScreeningFileMutation.isPending}
            />
          </Stack>
        </form>
        <ShowCountryCodesDialog open={isCountriesDialogOpen} onClose={() => setIsCountriesDialogOpen(false)} />
      </ModalDialog>
    </Modal>
  );
}
